import { css } from '@emotion/css';
import * as React from 'react';

import useFeatureFlagsV2 from 'ms-helpers/useFeatureFlagsV2';
import {
  borderRadiusUI,
  fontFamily,
  fontSize,
  fontWeight,
} from 'ms-styles/base';
import { colors } from 'ms-styles/colors';
import { Logger } from 'ms-utils/app-logging';
import { useUnmountEffect } from 'ms-utils/hooks/useUnmountEffect';
import { unwrap } from 'ms-utils/typescript-utils';

import { DesmosCalculator } from './DesmosCalculator';

export type CalculatorKind =
  | 'scientific'
  | 'graphing'
  | 'geometry'
  | '3d'
  | 'statistics';

export const calculatorKinds: CalculatorKind[] = [
  'scientific',
  'graphing',
  'geometry',
  '3d',
  'statistics',
];

type Layout = {
  x: number;
  y: number;
  width: number;
  height: number;
};

export const headerHeight = 40;
let lastLayout: Layout | null = null;

// NOTE: This is not a typo - 'radiant' is what Geogebra actually uses
export type AngleUnit = 'degree' | 'radiant';

type CalculatorPopoverProps = {
  kind: CalculatorKind;
  angleUnit: AngleUnit;
  onClose: () => void;
  onChangeCalculator: (calculatorKind: CalculatorKind) => void;
  onChangeAngleUnit: (angleUnit: AngleUnit) => void;
};

export type CalculatorPopoverImperativeHandle = {
  resetLayout: () => void;
};

export const CalculatorPopover = React.forwardRef<
  CalculatorPopoverImperativeHandle,
  CalculatorPopoverProps
>(function _CalculatorPopover(
  { kind, angleUnit, onClose, onChangeCalculator, onChangeAngleUnit },
  ref,
) {
  const appletConfig = getAppletConfig(kind);
  const [layout, setLayout] = React.useState<Layout>(() =>
    getInitialLayout(appletConfig),
  );
  React.useImperativeHandle(ref, () => ({
    resetLayout: () => {
      setLayout(getDefaultLayout(appletConfig));
    },
  }));
  function cacheLayout() {
    lastLayout = {
      x: layout.x,
      y: layout.y,
      width: layout.width,
      height: layout.height,
    };
  }
  React.useEffect(cacheLayout, [layout]);
  const [isDragging, setIsDragging] = React.useState(false);
  const [isResizing, setIsResizing] = React.useState(false);
  const calculatorRef = React.useRef<ImperativeHandle | null>(null);
  const [calcIsLoading, setCalcIsLoading] = React.useState(true);

  return (
    <div
      style={{
        position: 'fixed',
        zIndex: 40,
        left: layout.x,
        top: layout.y,
        width: layout.width,
        height: layout.height,
        fontFamily: fontFamily.body,
      }}
    >
      {/* The clipped popover is a child as we need the PopoverResizeHandles to
          not get clipped */}
      <div
        style={{
          filter: 'drop-shadow(rgba(0, 0, 0, 0.05) 2px 4px 15px)',
          border: '1px solid #eee',
          background: 'white',
          borderRadius: 8,
          overflow: 'hidden',
          width: '100%',
          height: '100%',
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          style={{
            height: headerHeight,
            borderRadius: '8px 8px 0 0',
            background: '#FCFCFC',
            display: 'grid',
            cursor: isDragging ? 'grabbing' : 'grab',
            borderBottom: '1px solid #eee',
            boxSizing: 'border-box',
            flexShrink: 0,
          }}
          onMouseDown={event => {
            if (event.button !== 0) {
              // Ignore the event if we aren't doing a "normal" click
              // (0 means normal click: eg single finger click on trackpad, left
              // button on normal mice). Common "non-normal" clicks include
              // two-finger click on trackpad, or right click on a mouse.
              return;
            }
            if (event.ctrlKey) {
              // Ctrl click another way to do "right" click on macOS. In the
              // early days, Macs came with 1-button mice, so this was the only
              // way to do a "right" click.
              return;
            }
            setIsDragging(true);
            const startLayout = { x: layout.x, y: layout.y };
            const dragStart = { x: event.clientX, y: event.clientY };

            function mouseMove(event: MouseEvent) {
              // Ensure that text selections don't occur when we are dragging
              // our cursor around to reposition the popover.
              event.preventDefault();

              const { innerWidth, innerHeight } = window;
              // Ensure that the delta from the grab origin is clamped to lie within the
              // bounds of the client surface of the browser. This means that when your
              // grab point on the popover leaves the window, translations will stop.
              const deltaX = clamp(
                -dragStart.x,
                innerWidth - dragStart.x,
                event.clientX - dragStart.x,
              );
              // Similar to the above clamping, except with the additional constraint
              // that you cannot drag *any* part of the top of the popover outside
              // the top of the client surface of the browser.
              const deltaY = clamp(
                -startLayout.y,
                innerHeight - dragStart.y,
                event.clientY - dragStart.y,
              );
              setLayout(l => ({
                ...l,
                x: startLayout.x + deltaX,
                y: startLayout.y + deltaY,
              }));
            }

            window.addEventListener('mousemove', mouseMove);
            window.addEventListener(
              'mouseup',
              () => {
                window.removeEventListener('mousemove', mouseMove);
                setIsDragging(false);
              },
              { once: true },
            );
          }}
          onTouchStart={event => {
            event.preventDefault();
            const { touches } = event;
            const touch = unwrap(touches[0]);

            setIsDragging(true);
            const startLayout = { x: layout.x, y: layout.y };
            const dragStart = { x: touch.clientX, y: touch.clientY };

            function touchMove(event: TouchEvent) {
              event.preventDefault();
              event.stopPropagation();
              const { touches } = event;
              const touch = unwrap(touches[0]);

              const deltaX = touch.clientX - dragStart.x;
              const deltaY = touch.clientY - dragStart.y;
              setLayout(l => ({
                ...l,
                x: startLayout.x + deltaX,
                // Ensure we can't drag the popover above the top of the window
                // and lose the top-bar controls (matches macOS behaviour)
                y: Math.max(0, startLayout.y + deltaY),
              }));
            }
            const touchMoveOpts: AddEventListenerOptions = { passive: false };
            const touchCancelOrEndOpts: AddEventListenerOptions = {
              once: true,
            };
            function touchEnd() {
              window.removeEventListener('touchmove', touchMove, touchMoveOpts);
              window.removeEventListener(
                'touchcancel',
                touchCancel,
                touchCancelOrEndOpts,
              );
              setIsDragging(false);
            }
            function touchCancel() {
              window.removeEventListener('touchmove', touchMove, touchMoveOpts);
              window.removeEventListener(
                'touchend',
                touchEnd,
                touchCancelOrEndOpts,
              );
              setIsDragging(false);
            }

            window.addEventListener('touchmove', touchMove, touchMoveOpts);
            window.addEventListener('touchend', touchEnd, touchCancelOrEndOpts);
            window.addEventListener(
              'touchcancel',
              touchCancel,
              touchCancelOrEndOpts,
            );
          }}
        >
          {isIOS() && (
            <DragIndicatorForIOSIcon
              style={{
                gridColumn: '1',
                gridRow: '1',
                justifySelf: 'center',
                alignSelf: 'center',
              }}
            />
          )}

          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              paddingLeft: isIOS() ? 11 : 5,
              paddingRight: isIOS() ? 11 : 5,
              gridColumn: '1',
              gridRow: '1',
            }}
          >
            <CalculatorSelector
              selectedCalculator={kind}
              onSelect={calculator => {
                setCalcIsLoading(true);
                onChangeCalculator(calculator);
              }}
            />

            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <AngleUnitSelect
                disabled={calcIsLoading}
                value={angleUnit}
                onChange={onChangeAngleUnit}
              />

              <div
                style={{
                  width: 16 + 2 * 5,
                  height: 16 + 2 * 10,
                  cursor: 'pointer',
                  alignItems: 'center',
                  justifyContent: 'center',
                  display: 'flex',
                }}
                onMouseDown={() => {
                  if (calculatorRef.current === null) return;
                  calculatorRef.current.reset();
                }}
              >
                <ResetIcon />
              </div>

              <div
                style={{
                  width: 16 + 2 * 5,
                  height: 16 + 2 * 10,
                  cursor: 'pointer',
                  alignItems: 'center',
                  justifyContent: 'center',
                  display: 'flex',
                }}
                onMouseDown={() => {
                  cacheLayout();
                  onClose();
                }}
              >
                <CloseIcon />
              </div>
            </div>
          </div>
        </div>
        <div
          style={{
            // Geogebra is in iframes so you get weird event delegation if your
            // mouse goes in there during a drag event so we ensure the entire
            // subtree doesn't receive events while we drag/resize.
            pointerEvents: isDragging || isResizing ? 'none' : 'auto',
            height: layout.height - headerHeight,
            overflow: 'hidden',
            // Ensure any zIndexes used inside Geogebra don't interact
            // with layering outside of this subtree.
            isolation: 'isolate',
          }}
        >
          <CalculatorOrchestrator
            key={kind}
            calculatorRef={calculatorRef}
            kind={kind}
            layout={layout}
            angleUnit={angleUnit}
            onLoad={() => {
              setCalcIsLoading(false);
            }}
            onChangeAngleUnit={onChangeAngleUnit}
          />
        </div>
      </div>
      <PopoverResizeHandles
        layout={layout}
        setLayout={setLayout}
        onResizeStart={() => {
          setIsResizing(true);
        }}
        onResizeEnd={() => {
          setIsResizing(false);
        }}
      />
    </div>
  );
});

function getDefaultLayout(appletConfig: AppletConfig): Layout {
  return {
    // Default to 10px below nav, 10px away from right edge
    x: window.innerWidth - appletConfig.width - 10,
    y: 64 + 10, // 64 is header height
    width: appletConfig.width,
    height: appletConfig.height,
  };
}

function getInitialLayout(appletConfig: AppletConfig): Layout {
  const { innerWidth: width, innerHeight: height } = window;
  const windowRect = { x: 0, y: 0, width, height };
  // If the lastLayout lies entirely outside the viewport we'll use the
  // default instead. Main time this may occur is if the user drastically
  // shrunk the browser window since they last opened the calculator.
  if (lastLayout && rectanglesIntersect(lastLayout, windowRect)) {
    return lastLayout;
  }
  return getDefaultLayout(appletConfig);
}

function rectanglesIntersect(r1: Layout, r2: Layout) {
  if (r1.x + r1.width < r2.x || r1.x > r2.x + r2.width) return false;
  if (r1.y + r1.height < r2.y || r1.y > r2.y + r2.height) return false;
  return true;
}

export function CalculatorSelector({
  selectedCalculator,
  onSelect,
}: {
  selectedCalculator: CalculatorKind;
  onSelect: (calculatorKind: CalculatorKind) => void;
}) {
  const [isOpen, setIsOpen] = React.useState(false);
  const node = React.useRef<HTMLDivElement | null>(null);

  type CalculatorConfig = {
    kind: CalculatorKind;
    title: string;
    icon: React.ReactElement;
  };

  const options: Record<CalculatorKind, CalculatorConfig> = {
    scientific: {
      kind: 'scientific',
      title: 'Scientific',
      icon: <ScientificOptionIcon />,
    },
    graphing: {
      kind: 'graphing',
      title: 'Graphing',
      icon: <GraphingOptionIcon />,
    },
    geometry: {
      kind: 'geometry',
      title: 'Geometry',
      icon: <GeometryOptionIcon />,
    },
    '3d': { kind: '3d', title: '3d', icon: <ThreeDOptionIcon /> },
    statistics: {
      kind: 'statistics',
      title: 'Statistics',
      icon: <StatisticsOptionIcon />,
    },
  };

  const optionsList = Object.values(options);
  const selectedOption = options[selectedCalculator];

  // NOTE one downside with this technique is there is a bunch of browser
  // stuff that is stupidly "hardcoded" and doesn't interact with the event
  // system. Eg. the "active" state when mousedown on an <a> tag occurs. There
  // are also other things like the OS cursor that lack any ergonomic API for
  // controlling. The only robust way is probably to cover the entire screen
  // with a transparent div that captures all tap-related events. But this
  // screws how we do positioning relative to the anchor since it requires
  // everthing existing in an unrelated position of the DOM tree to get
  // layering to work.
  React.useEffect(() => {
    if (node.current === null) return;
    const _node = node.current; // Fixes TS bug that loses narrowing

    const isTouchDevice = window.matchMedia('(pointer: coarse)').matches;
    const tapEventNames = isTouchDevice
      ? (['pointerdown', 'pointerup', 'touchstart'] as const)
      : (['pointerdown', 'pointerup', 'mousedown', 'mouseup'] as const);
    const finalTapEvent = isTouchDevice ? 'touchend' : 'click';

    function preventTapOutside(event: PointerEvent | TouchEvent | MouseEvent) {
      // We want to prevent all forms of tap events from being observed
      // when the popover is open. Otherwise tapping to dismiss the popover
      // can trigger additional actions to take place.
      if (!(event.target instanceof Node)) return;
      if (_node.contains(event.target) === false) {
        event.preventDefault();
        event.stopPropagation();
      }
    }
    for (const eventName of tapEventNames) {
      window.addEventListener(eventName, preventTapOutside, {
        capture: true,
      });
    }

    function onFinalTapEvent(event: TouchEvent | MouseEvent) {
      if (!(event.target instanceof Node)) return;
      if (_node.contains(event.target) === false) {
        event.preventDefault(); // ensure tapping <a> tags aren't actioned
        event.stopPropagation();
        setIsOpen(false);
      }
    }
    window.addEventListener(finalTapEvent, onFinalTapEvent, { capture: true });

    return () => {
      for (const eventName of tapEventNames)
        window.removeEventListener(eventName, preventTapOutside, {
          capture: true,
        });
      window.removeEventListener(finalTapEvent, onFinalTapEvent, {
        capture: true,
      });
    };
  }, [isOpen]);

  return (
    <div
      style={{ position: 'relative' }}
      // We don't want the event to bubble to the grabbable header in which we
      // sit as otherwise you can perform drags when interacting with the selector
      onMouseDown={e => e.stopPropagation()}
      onTouchStart={e => e.stopPropagation()}
    >
      <button
        style={{
          color: colors.eggplant,
          border: 'none',
          background: 'none',
          padding: '0 5px',
          display: 'flex',
          alignItems: 'center',
          fontSize: 16,
          fontWeight: 600,
          cursor: 'pointer',
          fontFamily: fontFamily.body,
        }}
        onClick={() => {
          setIsOpen(o => !o);
        }}
      >
        {selectedOption.icon}
        <div style={{ width: 5 }} />
        {selectedOption.title}
        <ChevronIcon isOpen={isOpen} />
      </button>
      {isOpen && (
        <div
          ref={node}
          style={{
            position: 'absolute',
            top: 27,
            left: 0,
            background: colors.white,
            borderRadius: 8,
            filter: 'drop-shadow(0 3px 6px rgba(0,0,0,.15))',
            zIndex: 101, // Ensure that this beats any geogebra applet elements
            padding: '5px 0',
          }}
        >
          {optionsList.map(({ kind, title, icon }) => (
            <CalculatorOption
              key={kind}
              icon={icon}
              title={title}
              isSelected={selectedCalculator === kind}
              onSelect={() => {
                setIsOpen(false);
                onSelect(kind);
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function CalculatorOption({
  icon,
  title,
  isSelected,
  onSelect,
}: {
  icon: JSX.Element;
  title: string;
  isSelected: boolean;
  onSelect: () => void;
}) {
  return (
    <div
      onClick={() => {
        onSelect();
      }}
      style={{
        display: 'flex',
        gap: 10,
        alignItems: 'center',
        padding: '8px 15px',
        cursor: 'pointer',
      }}
    >
      <div style={{ color: isSelected ? colors.eggplant : colors.grey10 }}>
        {icon}
      </div>
      <div
        style={{
          color: isSelected ? colors.eggplant : colors.grey90,
          fontWeight: isSelected ? '600' : '400',
        }}
      >
        {title}
      </div>
    </div>
  );
}

const angleUnitSelectCss = css({
  border: `1px solid ${colors.ironLight}`,
  borderRadius: borderRadiusUI,
  color: colors.eggplant,
  fontFamily: 'inherit',
  fontSize: fontSize.xSmall,
  fontWeight: fontWeight.semibold,

  ':focus': {
    borderColor: colors.eggplant,
    boxShadow: `0 0 0 3px ${colors.eggplant20}`,
    outline: 0,
  },
});

export function AngleUnitSelect({
  disabled,
  value,
  onChange,
}: {
  disabled?: boolean | undefined;
  value: AngleUnit;
  onChange: (angleUnit: AngleUnit) => void;
}) {
  return (
    <select
      aria-label="Angle unit"
      disabled={disabled}
      className={angleUnitSelectCss}
      value={value}
      onChange={e => {
        onChange(e.target.value as AngleUnit);
      }}
    >
      <option value="degree">Degrees</option>
      <option value="radiant">Radians</option>
    </select>
  );
}

type CalculatorOrchestratorProps = {
  kind: CalculatorKind;
  // Required by Geogebra calculators as they have to be manually resized
  // to match their container. No such requirement for Desmos calculators.
  layout: Layout;
  // Various calculators will default to 'degree' if nothing is provided.
  angleUnit?: AngleUnit | undefined;
  // Desmos calculators can change the angle unit internally, so we must
  // propagate this up the tree to keep things in sync.
  onChangeAngleUnit: (angleUnit: AngleUnit) => void;
  // This props is primarily because the loading process of Geogebra calculators
  // is slow. While we call this "onLoad", it's not necessarily indicative of
  // indicative of the applet being fully loaded/interactive;
  // only that the callback supplied to appletOnLoad has been invoked.
  // See: https://wiki.geogebra.org/en/Reference:GeoGebra_App_Parameters
  onLoad?: (() => void) | undefined;
  calculatorRef?: React.Ref<ImperativeHandle> | undefined;
};

export type ImperativeHandle = { reset: () => void };

// We have two very distinct 'scientific', and 'graphing' calculators.
// Ones made by Geogebra, and ones made by Desmos. Only a special subset
// of users get the Desmos calculator (at time of writing Virginia students).
// This component is responsible for selecting the appropriate implementation
// and papering over the API differences.
export function CalculatorOrchestrator(props: CalculatorOrchestratorProps) {
  const { workbookEnableDesmosCalculators } = useFeatureFlagsV2();

  if (
    workbookEnableDesmosCalculators &&
    (props.kind === 'scientific' || props.kind === 'graphing')
  ) {
    return (
      <DesmosCalculator
        kind={props.kind}
        angleUnit={props.angleUnit}
        onChangeAngleUnit={props.onChangeAngleUnit}
        calculatorRef={props.calculatorRef}
        onLoad={props.onLoad}
      />
    );
  } else {
    return (
      <GeogebraCalculator
        calculatorRef={props.calculatorRef}
        kind={props.kind}
        layout={props.layout}
        angleUnit={props.angleUnit}
        onLoad={props.onLoad}
      />
    );
  }
}

// Used to cache the initial ggb state of a given calculator so that we
// can allow users to reset the calculator to the initial state.
const initialGgbCache: Partial<Record<CalculatorKind, string>> = {};
// Used to cache the latest ggb state of a given calculator when the user
// closes/switches away from a given calculator so that we can restore the
// state when they return to that calculator kind
const calculatorGgbCache: Partial<Record<CalculatorKind, string>> = {};

function GeogebraCalculator({
  kind,
  layout,
  angleUnit = 'degree',
  onLoad,
  calculatorRef,
}: {
  kind: CalculatorKind;
  layout: Layout;
  angleUnit?: AngleUnit | undefined;
  onLoad?: (() => void) | undefined;
  calculatorRef?: React.Ref<ImperativeHandle> | undefined;
}) {
  const mountNode = React.useRef(null);
  type AppletObject = {
    setSize(width: number, height: number): number;
    getBase64(): string;
    setBase64(base64: string, callback?: () => void): string;
    getXML(): string;
    setXML(xmlString: string): void;
  };
  const appletObject = React.useRef<AppletObject | null>(null);
  const layoutChangeDuringLoad = React.useRef<Layout | null>(null);

  React.useEffect(() => {
    let isStaleEffect = false;
    loadBootstrapScript()
      .then(GGBApplet => {
        if (isStaleEffect) return;
        const appletConfig = getAppletConfig(kind);
        const cachedGgb = calculatorGgbCache[kind];
        const applet = new GGBApplet({
          ...appletConfig,
          borderColor: '#F000', // transparent
          disableAutoScale: true,
          appletOnLoad(_appletObject: AppletObject) {
            if (cachedGgb === undefined) {
              // This is the first time the given calculator kind is being
              // loaded, so we store the initial ggb for future resetting.
              initialGgbCache[kind] = _appletObject.getBase64();
            }
            if (layoutChangeDuringLoad.current !== null) {
              const { width, height } = layoutChangeDuringLoad.current;
              _appletObject.setSize(width, height - headerHeight);
              layoutChangeDuringLoad.current = null;
            } else {
              _appletObject.setSize(layout.width, layout.height - headerHeight);
            }
            appletObject.current = _appletObject;
            setAngleUnit(angleUnit);
            onLoad?.();
          },
          // Initialize the applet to the last cachedGgb if it exists
          ...(cachedGgb ? { ggbBase64: cachedGgb } : {}),
        });
        applet.inject(mountNode.current);
      })
      .catch(error => {
        console.error(error); // eslint-disable-line
        // TODO
      });
    return () => {
      isStaleEffect = true;
    };
    // Initializing an applet is very expensive operation. We only want to do
    // this when the .ggb needs to change (which is when the kind changes).
    // 🚨 Do NOT change this dep array to include anything else.
  }, [kind]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useImperativeHandle(calculatorRef, () => ({
    reset: () => {
      if (appletObject.current === null) return;
      const initialState = initialGgbCache[kind];
      if (initialState === undefined) return;
      appletObject.current.setBase64(initialState, () => {
        setAngleUnit(angleUnit);
      });
    },
  }));

  React.useEffect(() => {
    if (appletObject.current === null) {
      // We're currently loading the applet which can take ages so
      // store the layout change so we can apply it once the applet
      // object is finally ready.
      layoutChangeDuringLoad.current = layout;
    } else {
      appletObject.current.setSize(layout.width, layout.height - headerHeight);
    }
  }, [layout]);

  useUnmountEffect(() => {
    if (appletObject.current === null) return;
    calculatorGgbCache[kind] = appletObject.current.getBase64();
  });

  const setAngleUnit = React.useCallback((angleUnit: AngleUnit) => {
    if (appletObject.current === null) return;
    const xmlString = appletObject.current.getXML();
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xmlString, 'application/xml');
    const errorNode = xmlDoc.querySelector('parsererror');
    const angleUnitNode = xmlDoc.querySelector('angleUnit');

    if (errorNode) {
      Logger.error('Error parsing Geogebra applet XML', {
        extra: { xmlString },
      });
      return;
    }

    if (angleUnitNode === null) {
      Logger.error('<angleUnit> node not found in Geogebra applet XML', {
        extra: { angleUnit, xmlString },
      });
      return;
    }

    // Bail early to prevent expensive operation if the angle unit is already
    // set to the desired value.
    if (angleUnit === angleUnitNode.getAttribute('val')) return;

    angleUnitNode.setAttribute('val', angleUnit);
    const serializer = new XMLSerializer();
    const serializedXml = serializer.serializeToString(xmlDoc);
    appletObject.current.setXML(serializedXml);
  }, []);

  React.useEffect(() => {
    setAngleUnit(angleUnit);
  }, [angleUnit, setAngleUnit]);

  return <div ref={mountNode} />;
}

type AppletConfig = {
  id: string;
  width: number;
  height: number;
  appName?: string;
  material_id?: string;
  showAlgebraInput?: boolean;
  showToolBar?: boolean;
  showToolBarHelp?: boolean;
  showMenuBar?: boolean;
};
function getAppletConfig(kind: CalculatorKind): AppletConfig {
  const normalDimensions = { width: 745, height: 575 };

  const configs = {
    scientific: {
      id: 'scientific',
      appName: 'scientific',
      showAlgebraInput: true,
      ...normalDimensions,
    },
    geometry: {
      id: 'geometry',
      appName: 'geometry',
      showToolBar: true,
      showAlgebraInput: true,
      ...normalDimensions,
    },
    graphing: {
      id: 'graphing',
      appName: 'graphing',
      showToolBar: true,
      showMenuBar: false,
      showAlgebraInput: true,
      showToolBarHelp: false,
      ...normalDimensions,
    },
    statistics: {
      id: 'statistics',
      material_id: 'kxctvxq7',
      showToolBarHelp: false,
      // The statistics applet works better at these default dimensions
      width: 745,
      height: 700,
    },
    '3d': {
      id: '3d',
      appName: '3d',
      showAlgebraInput: true,
      showToolBar: true,
      ...normalDimensions,
    },
  } as const;

  return configs[kind];
}

type ResizeHandleLocation = (typeof resizeHandleLocations)[number];
const resizeHandleLocations = [
  'top',
  'right',
  'bottom',
  'left',
  // Corners come after so they are stacked above
  'topLeft',
  'topRight',
  'bottomRight',
  'bottomLeft',
] as const;

function PopoverResizeHandles({
  layout,
  setLayout,
  onResizeStart,
  onResizeEnd,
}: {
  layout: Layout;
  setLayout: React.Dispatch<React.SetStateAction<Layout>>;
  onResizeStart: () => void;
  onResizeEnd: () => void;
}) {
  // Our version of typescript sucks and has incorrect React types so we have
  // to wrap this in an uneccessary fragment.
  return (
    <>
      {resizeHandleLocations
        .filter(place => {
          if (
            isIOS() &&
            (place === 'topLeft' || place === 'top' || place === 'topRight')
          ) {
            // We'll get rid of the resizing at the top bar when on iOS as you
            // unintentionally cause resizes when trying to drag the top bar
            return false;
          }
          return true;
        })
        .map(location => (
          <PopoverResizeHandle
            key={location}
            location={location}
            layout={layout}
            setLayout={setLayout}
            onResizeStart={onResizeStart}
            onResizeEnd={onResizeEnd}
          />
        ))}
    </>
  );
}

function PopoverResizeHandle({
  location,
  layout,
  setLayout,
  onResizeStart,
  onResizeEnd,
}: {
  location: ResizeHandleLocation;
  layout: Layout;
  setLayout: React.Dispatch<React.SetStateAction<Layout>>;
  onResizeStart: () => void;
  onResizeEnd: () => void;
}) {
  const minWidth = 350;
  const minHeight = 350;
  const edgeWidth = isIOS() ? 25 : 10;
  const cornerStyle = {
    width: isIOS() ? 25 : 20,
    height: isIOS() ? 25 : 20,
    zIndex: 1,
  };
  const top = -(edgeWidth / 2);
  const left = -(edgeWidth / 2);
  const right = -(edgeWidth / 2);
  const bottom = -(edgeWidth / 2);
  const layoutStyle = {
    topLeft: { top, left, ...cornerStyle },
    top: { top, left, width: '100%', height: edgeWidth },
    topRight: { top, right, ...cornerStyle },
    right: { top, right, width: edgeWidth, height: '100%' },
    bottomRight: { bottom, right, ...cornerStyle },
    bottom: { bottom, left, width: '100%', height: edgeWidth },
    bottomLeft: { bottom, left, ...cornerStyle },
    left: { top, left, width: edgeWidth, height: '100%' },
  }[location];

  const cursor = {
    topLeft: 'nwse-resize',
    top: 'ns-resize',
    topRight: 'nesw-resize',
    right: 'ew-resize',
    bottomRight: 'nwse-resize',
    bottom: 'ns-resize',
    bottomLeft: 'nesw-resize',
    left: 'ew-resize',
  }[location];

  function makeMoveHandler(
    startLayout: Layout,
    startPos: { x: number; y: number },
  ) {
    return function moveHandler(event: MouseEvent | TouchEvent) {
      // Ensure that text selections don't occur when we are dragging
      // our cursor around to resize the popover.
      event.preventDefault();

      const x =
        event instanceof MouseEvent
          ? event.clientX
          : event.touches[0]?.clientX ?? 0;
      const y =
        event instanceof MouseEvent
          ? event.clientY
          : event.touches[0]?.clientY ?? 0;
      const vx = x - startPos.x;
      const vy = y - startPos.y;

      let nextLayout;
      switch (location) {
        case 'topLeft': {
          const clampedX = clamp(
            -startLayout.x,
            startLayout.width - minWidth,
            vx,
          );
          const clampedY = clamp(
            -startLayout.y,
            startLayout.height - minHeight,
            vy,
          );
          nextLayout = {
            x: startLayout.x + clampedX,
            y: startLayout.y + clampedY,
            width: startLayout.width - clampedX,
            height: startLayout.height - clampedY,
          };
          break;
        }
        case 'topRight': {
          const clampedX = clamp(
            -(startLayout.width - minWidth),
            window.innerWidth - (startLayout.x + startLayout.width),
            vx,
          );
          const clampedY = clamp(
            -startLayout.y,
            startLayout.height - minHeight,
            vy,
          );

          nextLayout = {
            x: startLayout.x,
            y: startLayout.y + clampedY,
            width: startLayout.width + clampedX,
            height: startLayout.height - clampedY,
          };
          break;
        }
        case 'bottomRight': {
          const clampedX = clamp(
            -(startLayout.width - minWidth),
            window.innerWidth - (startLayout.x + startLayout.width),
            vx,
          );
          const clampedY = clamp(
            -(startLayout.height - minHeight),
            window.innerHeight - (startLayout.y + startLayout.height),
            vy,
          );
          nextLayout = {
            x: startLayout.x,
            y: startLayout.y,
            width: startLayout.width + clampedX,
            height: startLayout.height + clampedY,
          };
          break;
        }
        case 'bottomLeft': {
          const clampedX = clamp(
            -startLayout.x,
            startLayout.width - minWidth,
            vx,
          );
          const clampedY = clamp(
            -(startLayout.height - minHeight),
            window.innerHeight - (startLayout.y + startLayout.height),
            vy,
          );
          nextLayout = {
            x: startLayout.x + clampedX,
            y: startLayout.y,
            width: startLayout.width - clampedX,
            height: startLayout.height + clampedY,
          };
          break;
        }

        case 'top': {
          const clampedY = clamp(
            -startLayout.y,
            startLayout.height - minHeight,
            vy,
          );
          nextLayout = {
            x: startLayout.x,
            y: startLayout.y + clampedY,
            width: startLayout.width,
            height: startLayout.height - clampedY,
          };
          break;
        }
        case 'right': {
          const clampedX = clamp(
            -(startLayout.width - minWidth),
            window.innerWidth - (startLayout.x + startLayout.width),
            vx,
          );
          nextLayout = {
            x: startLayout.x,
            y: startLayout.y,
            width: startLayout.width + clampedX,
            height: startLayout.height,
          };
          break;
        }
        case 'bottom': {
          const clampedY = clamp(
            -(startLayout.height - minHeight),
            window.innerHeight - (startLayout.y + startLayout.height),
            vy,
          );
          nextLayout = {
            x: startLayout.x,
            y: startLayout.y,
            width: startLayout.width,
            height: startLayout.height + clampedY,
          };
          break;
        }
        case 'left': {
          const clampedX = clamp(
            -startLayout.x,
            startLayout.width - minWidth,
            vx,
          );
          nextLayout = {
            x: startLayout.x + clampedX,
            y: startLayout.y,
            width: startLayout.width - clampedX,
            height: startLayout.height,
          };
          break;
        }
        default:
          throw Error('nextLayout not computed');
      }
      setLayout(nextLayout);
    };
  }

  // As far as I can tell, there is no robust programmatic way to specify
  // what the cursor should be. To ensure that your cursor remains correct
  // for the given resize operation you are performing, we cover the entire
  // client surface with a transparent div with the correct cursor specified.
  function appendCursorLockOverlay() {
    const overlay = document.createElement('div');
    overlay.style.position = 'fixed';
    overlay.style.top = '0px';
    overlay.style.right = '0px';
    overlay.style.bottom = '0px';
    overlay.style.left = '0px';
    overlay.style.zIndex = '40';
    overlay.style.cursor = cursor;
    if (document.body) {
      document.body.appendChild(overlay);
    }
    return overlay;
  }

  function _onResizeEnd(overlayNode?: HTMLDivElement) {
    if (overlayNode && document.body) document.body.removeChild(overlayNode);
    onResizeEnd();
  }

  return (
    <div
      style={{ position: 'absolute', zIndex: 1, ...layoutStyle, cursor }}
      onTouchStart={event => {
        event.preventDefault();
        const touch = unwrap(event.touches[0]);
        const startPos = { x: touch.clientX, y: touch.clientY };
        const touchMove = makeMoveHandler(layout, startPos);
        function touchEnd() {
          // @ts-expect-error TS is confused
          window.removeEventListener('touchmove', touchMove, {
            passive: false,
          });
          window.removeEventListener('touchend', touchEnd);
          window.removeEventListener('touchcancel', touchEnd);
          _onResizeEnd();
        }
        window.addEventListener('touchmove', touchMove, { passive: false });
        window.addEventListener('touchend', touchEnd);
        window.addEventListener('touchcancel', touchEnd);
        onResizeStart();
      }}
      onMouseDown={event => {
        const startPos = { x: event.clientX, y: event.clientY };
        const mouseMove = makeMoveHandler(layout, startPos);
        const overlayNode = appendCursorLockOverlay();
        window.addEventListener('mousemove', mouseMove);
        window.addEventListener(
          'mouseup',
          () => {
            window.removeEventListener('mousemove', mouseMove);
            _onResizeEnd(overlayNode);
          },
          { once: true },
        );
        onResizeStart();
      }}
    />
  );
}

function DragIndicatorForIOSIcon({ style }: { style: React.CSSProperties }) {
  return (
    <svg style={style} width="36" height="5" viewBox="0 0 36 5">
      <rect width="36" height="5" rx="2.5" fill="rgba(195, 196, 196)" />
    </svg>
  );
}

function ResetIcon() {
  return (
    <svg
      style={{ display: 'block' }}
      height="16px"
      viewBox="0 0 16 16"
      width="16px"
      fill={colors.shuttleGray}
    >
      <path d="M15.8023 0.864625C15.6705 0.732839 15.5142 0.666727 15.3336 0.666727C15.153 0.666727 14.9969 0.732839 14.8648 0.864625L13.5107 2.20857C12.7677 1.5071 11.9169 0.963574 10.9586 0.578249C10.0005 0.192888 9.01406 0.000244141 8.00013 0.000244141C6.09033 0.000244141 4.42373 0.578286 3.00007 1.73462C1.57636 2.89082 0.6389 4.39935 0.1875 6.26059V6.33345C0.1875 6.42372 0.220447 6.50191 0.286449 6.5678C0.352451 6.6338 0.430531 6.66675 0.520796 6.66675H2.59369C2.74653 6.66675 2.85062 6.58685 2.90623 6.42722C3.19785 5.73283 3.38182 5.3266 3.45829 5.20849C3.94435 4.41683 4.59018 3.79526 5.39578 3.34386C6.20146 2.8925 7.06949 2.66672 7.99998 2.66672C9.39596 2.66672 10.6077 3.14246 11.6356 4.09386L10.1978 5.53128C10.0662 5.66325 10 5.81959 10 6.00016C10 6.18069 10.0662 6.33696 10.1978 6.46889C10.33 6.60086 10.4863 6.66679 10.6668 6.66679H15.3336C15.5141 6.66679 15.6704 6.60086 15.8023 6.46889C15.9341 6.33696 16.0001 6.18073 16.0001 6.00016V1.33339C16.0001 1.15282 15.9345 0.99663 15.8023 0.864625Z" />
      <path d="M15.4062 9.33357H13.4062C13.2534 9.33357 13.1492 9.41336 13.0938 9.57313C12.8023 10.2676 12.6181 10.6736 12.5418 10.7918C12.0556 11.5832 11.4097 12.205 10.6043 12.6564C9.79861 13.1079 8.93058 13.3334 8.00006 13.3334C7.32649 13.3334 6.67712 13.2085 6.0522 12.9586C5.4272 12.7084 4.86809 12.3541 4.3751 11.8958L5.80223 10.4687C5.93413 10.3369 6.00013 10.1805 6.00013 10C6.00013 9.81948 5.93413 9.66303 5.80223 9.53128C5.67027 9.39921 5.51407 9.33313 5.3335 9.33313H0.666738C0.486061 9.33313 0.329793 9.39921 0.197861 9.53128C0.0659295 9.66307 0 9.81952 0 10.0001V14.6668C0 14.8472 0.0659295 15.0037 0.197898 15.1355C0.329939 15.2673 0.486207 15.3336 0.666775 15.3336C0.847305 15.3336 1.0035 15.2673 1.13551 15.1355L2.47923 13.7917C3.22234 14.4999 4.06789 15.0451 5.01582 15.4271C5.96376 15.8091 6.94467 15.9999 7.9585 15.9999C9.86141 15.9999 11.5211 15.4218 12.9377 14.2656C14.3544 13.1095 15.285 11.6006 15.7296 9.73958C15.7366 9.72572 15.7399 9.70138 15.7399 9.66679C15.7399 9.57638 15.7068 9.49852 15.6408 9.4323C15.5748 9.36666 15.4966 9.33357 15.4062 9.33357Z" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg
      style={{ display: 'block' }}
      height="16px"
      viewBox="0 0 16 16"
      width="16px"
      fill={colors.shuttleGray}
    >
      <path d="M10.476 8.00358L15.4806 3.01243C15.6443 2.84857 15.7742 2.65403 15.8628 2.43994C15.9514 2.22584 15.997 1.99637 15.997 1.76464C15.997 1.5329 15.9514 1.30344 15.8628 1.08934C15.7742 0.875246 15.6443 0.680712 15.4806 0.51685C15.3169 0.352988 15.1225 0.223006 14.9086 0.134325C14.6946 0.0456435 14.4654 0 14.2338 0C14.0023 0 13.773 0.0456435 13.5591 0.134325C13.3452 0.223006 13.1508 0.352988 12.9871 0.51685L8 5.52558L3.01295 0.51685C2.84922 0.352988 2.65485 0.223006 2.44093 0.134325C2.22701 0.0456435 1.99773 0 1.76619 0C1.53464 0 1.30536 0.0456435 1.09144 0.134325C0.877524 0.223006 0.68315 0.352988 0.519423 0.51685C0.355696 0.680712 0.225821 0.875246 0.137212 1.08934C0.0486036 1.30344 0.00299759 1.5329 0.00299759 1.76464C0.0029976 1.99637 0.0486036 2.22584 0.137212 2.43994C0.225821 2.65403 0.355696 2.84857 0.519423 3.01243L5.52403 8.00358L0.519423 12.9947C0.354835 13.1581 0.2242 13.3525 0.13505 13.5666C0.0458997 13.7808 0 14.0105 0 14.2425C0 14.4745 0.0458997 14.7042 0.13505 14.9184C0.2242 15.1326 0.354835 15.3269 0.519423 15.4903C0.683504 15.6532 0.878098 15.7821 1.09204 15.8695C1.30599 15.957 1.53508 16.0013 1.76619 16C1.99729 16.0013 2.22638 15.957 2.44033 15.8695C2.65427 15.7821 2.84887 15.6532 3.01295 15.4903L8 10.4816L12.9871 15.4903C13.1511 15.6532 13.3457 15.7821 13.5597 15.8695C13.7736 15.957 14.0027 16.0013 14.2338 16C14.4649 16.0013 14.694 15.957 14.908 15.8695C15.1219 15.7821 15.3165 15.6532 15.4806 15.4903C15.6452 15.3269 15.7758 15.1326 15.865 14.9184C15.9541 14.7042 16 14.4745 16 14.2425C16 14.0105 15.9541 13.7808 15.865 13.5666C15.7758 13.3525 15.6452 13.1581 15.4806 12.9947L10.476 8.00358Z" />
    </svg>
  );
}

function ChevronIcon({ isOpen }: { isOpen: boolean }) {
  return (
    <svg
      style={{ transform: `rotate(${isOpen ? 180 : 0}deg)` }}
      height="24px"
      viewBox="0 0 24 24"
      width="24px"
      fill={colors.shuttleGray}
    >
      <path d="M16.59 8.59L12 13.17 7.41 8.59 6 10l6 6 6-6-1.41-1.41z" />
    </svg>
  );
}

function ScientificOptionIcon() {
  return (
    <svg
      style={{ display: 'block' }}
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="currentColor"
    >
      <path d="M1.40625 7.53125H6.125C6.90044 7.53125 7.53125 6.90044 7.53125 6.125V1.40625C7.53125 0.630813 6.90044 0 6.125 0H1.40625C0.630813 0 0 0.630813 0 1.40625V6.125C0 6.90044 0.630813 7.53125 1.40625 7.53125ZM2.375 3.3125H3.3125V2.375C3.3125 2.11591 3.52216 1.90625 3.78125 1.90625C4.04034 1.90625 4.25 2.11591 4.25 2.375V3.3125H5.1875C5.4466 3.3125 5.65625 3.52216 5.65625 3.78125C5.65625 4.04034 5.4466 4.25 5.1875 4.25H4.25V5.1875C4.25 5.4466 4.04034 5.65625 3.78125 5.65625C3.52216 5.65625 3.3125 5.4466 3.3125 5.1875V4.25H2.375C2.11591 4.25 1.90625 4.04034 1.90625 3.78125C1.90625 3.52216 2.11591 3.3125 2.375 3.3125Z" />
      <path d="M14.5938 0H9.875C9.09956 0 8.46875 0.630813 8.46875 1.40625V6.125C8.46875 6.90044 9.09956 7.53125 9.875 7.53125H14.5938C15.3692 7.53125 16 6.90044 16 6.125V1.40625C16 0.630813 15.3692 0 14.5938 0ZM13.6562 4.25H10.8125C10.5534 4.25 10.3438 4.04034 10.3438 3.78125C10.3438 3.52216 10.5534 3.3125 10.8125 3.3125H13.6562C13.9153 3.3125 14.125 3.52216 14.125 3.78125C14.125 4.04034 13.9153 4.25 13.6562 4.25Z" />
      <path d="M14.5938 8.46875H9.875C9.09956 8.46875 8.46875 9.09956 8.46875 9.875V14.5938C8.46875 15.3692 9.09956 16 9.875 16H14.5938C15.3692 16 16 15.3692 16 14.5938V9.875C16 9.09956 15.3692 8.46875 14.5938 8.46875ZM13.6562 13.625H10.8125C10.5534 13.625 10.3438 13.4153 10.3438 13.1562C10.3438 12.8972 10.5534 12.6875 10.8125 12.6875H13.6562C13.9153 12.6875 14.125 12.8972 14.125 13.1562C14.125 13.4153 13.9153 13.625 13.6562 13.625ZM13.6562 11.75H10.8125C10.5534 11.75 10.3438 11.5403 10.3438 11.2812C10.3438 11.0222 10.5534 10.8125 10.8125 10.8125H13.6562C13.9153 10.8125 14.125 11.0222 14.125 11.2812C14.125 11.5403 13.9153 11.75 13.6562 11.75Z" />
      <path d="M7.53125 14.5938V9.875C7.53125 9.09956 6.90044 8.46875 6.125 8.46875H1.40625C0.630813 8.46875 0 9.09956 0 9.875V14.5938C0 15.3692 0.630813 16 1.40625 16H6.125C6.90044 16 7.53125 15.3692 7.53125 14.5938ZM5.10694 12.8816C5.29003 13.0647 5.29003 13.3613 5.10694 13.5444C4.92384 13.7275 4.62719 13.7275 4.44409 13.5444L3.78125 12.8816L3.11841 13.5444C2.93531 13.7275 2.63866 13.7275 2.45556 13.5444C2.27247 13.3613 2.27247 13.0647 2.45556 12.8816L3.11841 12.2188L2.45556 11.5559C2.27247 11.3728 2.27247 11.0762 2.45556 10.8931C2.63866 10.71 2.93531 10.71 3.11841 10.8931L3.78125 11.5559L4.44409 10.8931C4.62719 10.71 4.92384 10.71 5.10694 10.8931C5.29003 11.0762 5.29003 11.3728 5.10694 11.5559L4.44409 12.2188L5.10694 12.8816Z" />
    </svg>
  );
}

function GeometryOptionIcon() {
  return (
    <svg
      style={{ display: 'block' }}
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="currentColor"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M11.3147 1.36323C11.0351 0.878922 10.336 0.878922 10.0564 1.36323L7.4256 5.91995C6.72009 5.54677 5.91577 5.33545 5.06209 5.33545C2.26637 5.33545 0 7.60182 0 10.3975C0 13.1933 2.26637 15.4596 5.06209 15.4596C7.85775 15.4596 10.1241 13.1933 10.1242 10.3977H15.2725C15.8317 10.3977 16.1812 9.79231 15.9016 9.308L11.3147 1.36323ZM10.1241 10.3975C10.1241 8.45584 9.03084 6.76952 7.42632 5.92046L5.47063 9.30781C5.19102 9.79212 5.54054 10.3975 6.09978 10.3975H10.1241Z"
      />
    </svg>
  );
}
function GraphingOptionIcon() {
  return (
    <svg
      style={{ display: 'block' }}
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
    >
      <mask id="path-1-inside-1_299_3146" fill="white">
        <path d="M13.9425 10.5409C13.7579 10.3479 13.4496 10.3479 13.265 10.5409C13.0916 10.7222 13.0916 11.0078 13.265 11.189L13.5481 11.485H9.4112H8.11088V1.83607L8.36521 2.10194C8.54984 2.29495 8.85816 2.29495 9.04278 2.10194C9.21616 1.92068 9.21616 1.63507 9.04278 1.45382L8.37482 0.755489C7.98101 0.343786 7.32334 0.343786 6.92953 0.755489L6.26157 1.45382C6.08819 1.63507 6.08819 1.92068 6.26157 2.10194C6.44618 2.29495 6.75451 2.29495 6.93913 2.10195L7.19347 1.83607V11.485H5.89312H1.75624L2.03938 11.189C2.21274 11.0078 2.21274 10.7222 2.03937 10.5409C1.85475 10.3479 1.54641 10.3479 1.36179 10.5409L0.661157 11.2734C0.291352 11.66 0.291355 12.2692 0.661163 12.6558L1.36179 13.3883C1.54641 13.5813 1.85474 13.5813 2.03936 13.3883C2.21273 13.2071 2.21273 12.9215 2.03936 12.7402L1.75624 12.4442H7.19343V14.1639L6.9391 13.898C6.75448 13.705 6.44615 13.705 6.26153 13.898C6.08816 14.0793 6.08816 14.3649 6.26153 14.5461L6.9295 15.2445C7.3233 15.6562 7.98099 15.6562 8.37479 15.2445L9.04277 14.5461C9.21614 14.3649 9.21613 14.0793 9.04277 13.898C8.85814 13.705 8.54981 13.705 8.3652 13.8981L8.11091 14.1639V12.4442H13.5481L13.265 12.7402C13.0916 12.9215 13.0916 13.2071 13.265 13.3883C13.4496 13.5813 13.7579 13.5813 13.9425 13.3883L14.6432 12.6558C15.013 12.2692 15.013 11.66 14.6432 11.2734L13.9425 10.5409Z" />
      </mask>
      <path
        d="M13.9425 10.5409C13.7579 10.3479 13.4496 10.3479 13.265 10.5409C13.0916 10.7222 13.0916 11.0078 13.265 11.189L13.5481 11.485H9.4112H8.11088V1.83607L8.36521 2.10194C8.54984 2.29495 8.85816 2.29495 9.04278 2.10194C9.21616 1.92068 9.21616 1.63507 9.04278 1.45382L8.37482 0.755489C7.98101 0.343786 7.32334 0.343786 6.92953 0.755489L6.26157 1.45382C6.08819 1.63507 6.08819 1.92068 6.26157 2.10194C6.44618 2.29495 6.75451 2.29495 6.93913 2.10195L7.19347 1.83607V11.485H5.89312H1.75624L2.03938 11.189C2.21274 11.0078 2.21274 10.7222 2.03937 10.5409C1.85475 10.3479 1.54641 10.3479 1.36179 10.5409L0.661157 11.2734C0.291352 11.66 0.291355 12.2692 0.661163 12.6558L1.36179 13.3883C1.54641 13.5813 1.85474 13.5813 2.03936 13.3883C2.21273 13.2071 2.21273 12.9215 2.03936 12.7402L1.75624 12.4442H7.19343V14.1639L6.9391 13.898C6.75448 13.705 6.44615 13.705 6.26153 13.898C6.08816 14.0793 6.08816 14.3649 6.26153 14.5461L6.9295 15.2445C7.3233 15.6562 7.98099 15.6562 8.37479 15.2445L9.04277 14.5461C9.21614 14.3649 9.21613 14.0793 9.04277 13.898C8.85814 13.705 8.54981 13.705 8.3652 13.8981L8.11091 14.1639V12.4442H13.5481L13.265 12.7402C13.0916 12.9215 13.0916 13.2071 13.265 13.3883C13.4496 13.5813 13.7579 13.5813 13.9425 13.3883L14.6432 12.6558C15.013 12.2692 15.013 11.66 14.6432 11.2734L13.9425 10.5409Z"
        fill="currentColor"
      />
      <path
        d="M13.5481 11.485V13.485H18.2288L14.9934 10.1026L13.5481 11.485ZM8.11088 11.485H6.11088V13.485H8.11088V11.485ZM8.11088 1.83607L9.55613 0.453585L6.11088 -3.14808V1.83607H8.11088ZM7.19347 1.83607H9.19347V-3.14808L5.74822 0.453585L7.19347 1.83607ZM7.19347 11.485V13.485H9.19347V11.485H7.19347ZM1.75624 11.485L0.310919 10.1026L-2.92426 13.485H1.75624V11.485ZM1.75624 12.4442V10.4442H-2.92444L0.310957 13.8267L1.75624 12.4442ZM7.19343 12.4442H9.19343V10.4442H7.19343V12.4442ZM7.19343 14.1639L5.74815 15.5464L9.19343 19.1483V14.1639H7.19343ZM8.11091 14.1639H6.11091V19.1486L9.55627 15.5463L8.11091 14.1639ZM8.11091 12.4442V10.4442H6.11091V12.4442H8.11091ZM13.5481 12.4442L14.9934 13.8267L18.229 10.4442H13.5481V12.4442ZM8.3652 13.8981L9.81055 15.2804L8.3652 13.8981ZM9.04277 14.5461L7.59747 13.1637L9.04277 14.5461ZM8.37479 15.2445L6.9295 13.8621L8.37479 15.2445ZM2.03936 12.7402L0.59408 14.1227L2.03936 12.7402ZM1.36179 10.5409L2.80709 11.9233L1.36179 10.5409ZM2.03938 11.189L3.4847 12.5714L2.03938 11.189ZM14.6432 11.2734L13.1979 12.6558L14.6432 11.2734ZM6.93913 2.10195L5.49389 0.719463V0.719463L6.93913 2.10195ZM13.9425 13.3883L12.4973 12.0059L13.9425 13.3883ZM6.26157 1.45382L7.70685 2.83626L6.26157 1.45382ZM13.265 12.7402L14.7102 14.1227L13.265 12.7402ZM8.37482 0.755489L6.92953 2.13793L8.37482 0.755489ZM6.92953 0.755489L5.48425 -0.626956L6.92953 0.755489ZM1.36179 13.3883L-0.0834904 14.7708L1.36179 13.3883ZM9.04278 1.45382L7.5975 2.83626L9.04278 1.45382ZM0.661157 11.2734L-0.78414 9.89097L0.661157 11.2734ZM0.661163 12.6558L2.10645 11.2734L0.661163 12.6558ZM8.36521 2.10194L9.81046 0.719462L8.36521 2.10194ZM6.26153 14.5461L7.70682 13.1637L6.26153 14.5461ZM13.9425 10.5409L12.4972 11.9233L13.9425 10.5409ZM11.8197 12.5715L12.1028 12.8675L14.9934 10.1026L14.7102 9.80656L11.8197 12.5715ZM13.5481 9.48503H9.4112V13.485H13.5481V9.48503ZM10.1109 11.485V1.83607H6.11088V11.485H10.1109ZM6.66564 3.21855L6.91997 3.48443L9.81046 0.719462L9.55613 0.453585L6.66564 3.21855ZM10.4881 0.0713733L9.8201 -0.626957L6.92953 2.13793L7.5975 2.83626L10.4881 0.0713733ZM5.48425 -0.626956L4.81628 0.0713733L7.70685 2.83626L8.37482 2.13793L5.48425 -0.626956ZM8.38438 3.48443L8.63871 3.21855L5.74822 0.453585L5.49389 0.719463L8.38438 3.48443ZM5.19347 1.83607V11.485H9.19347V1.83607H5.19347ZM5.89312 9.48503H1.75624V13.485H5.89312V9.48503ZM3.20156 12.8674L3.4847 12.5714L0.594057 9.8066L0.310919 10.1026L3.20156 12.8674ZM-0.0835061 9.15847L-0.78414 9.89097L2.10645 12.6558L2.80709 11.9233L-0.0835061 9.15847ZM-0.78412 14.0383L-0.0834904 14.7708L2.80708 12.0059L2.10645 11.2734L-0.78412 14.0383ZM3.48464 11.3578L3.20152 11.0618L0.310957 13.8267L0.59408 14.1227L3.48464 11.3578ZM1.75624 14.4442H7.19343V10.4442H1.75624V14.4442ZM5.19343 12.4442V14.1639H9.19343V12.4442H5.19343ZM8.63872 12.7815L8.38439 12.5156L5.49382 15.2805L5.74815 15.5464L8.63872 12.7815ZM4.81623 15.9286L5.4842 16.6269L8.37479 13.8621L7.70682 13.1637L4.81623 15.9286ZM9.82009 16.6269L10.4881 15.9286L7.59747 13.1637L6.9295 13.8621L9.82009 16.6269ZM6.91984 12.5157L6.66556 12.7816L9.55627 15.5463L9.81055 15.2804L6.91984 12.5157ZM10.1109 14.1639V12.4442H6.11091V14.1639H10.1109ZM8.11091 14.4442H13.5481V10.4442H8.11091V14.4442ZM12.1029 11.0617L11.8197 11.3577L14.7102 14.1227L14.9934 13.8267L12.1029 11.0617ZM15.3878 14.7708L16.0884 14.0383L13.1979 11.2734L12.4973 12.0059L15.3878 14.7708ZM16.0885 9.89095L15.3878 9.15845L12.4972 11.9233L13.1979 12.6558L16.0885 9.89095ZM7.19347 9.48503H5.89312V13.485H7.19347V9.48503ZM9.4112 9.48503H8.11088V13.485H9.4112V9.48503ZM10.4881 12.5156C9.5158 11.4992 7.89205 11.4992 6.91984 12.5157L9.81055 15.2804C9.20757 15.9109 8.20049 15.9109 7.59748 15.2805L10.4881 12.5156ZM10.4881 15.9286C11.401 14.9741 11.401 13.4701 10.4881 12.5156L7.59748 15.2805C7.03123 14.6885 7.03123 13.7557 7.59747 13.1637L10.4881 15.9286ZM5.4842 16.6269C6.66561 17.8621 8.63867 17.8621 9.82009 16.6269L6.9295 13.8621C7.3233 13.4503 7.98099 13.4503 8.37479 13.8621L5.4842 16.6269ZM3.48465 14.7708C4.39764 13.8163 4.39764 12.3123 3.48464 11.3578L0.59408 14.1227C0.0278303 13.5307 0.0278281 12.5979 0.594077 12.0059L3.48465 14.7708ZM3.48465 9.15846C2.51242 8.14203 0.888719 8.14204 -0.0835061 9.15847L2.80709 11.9233C2.20411 12.5537 1.19707 12.5537 0.594083 11.9233L3.48465 9.15846ZM3.4847 12.5714C4.39765 11.6169 4.39763 10.1129 3.48465 9.15846L0.594083 11.9233C0.0278482 11.3314 0.0278351 10.3986 0.594057 9.8066L3.4847 12.5714ZM16.0884 14.0383C17.1979 12.8784 17.1979 11.0508 16.0885 9.89095L13.1979 12.6558C12.8281 12.2692 12.8281 11.66 13.1979 11.2734L16.0884 14.0383ZM4.81628 3.48438C5.78849 4.50078 7.41214 4.50081 8.38438 3.48443L5.49389 0.719463C6.09688 0.089097 7.10388 0.0891112 7.70685 0.719491L4.81628 3.48438ZM11.8197 14.7708C12.7919 15.7872 14.4156 15.7872 15.3878 14.7708L12.4973 12.0059C13.1002 11.3755 14.1073 11.3755 14.7102 12.0059L11.8197 14.7708ZM4.81628 0.0713733C3.90329 1.02587 3.90328 2.52988 4.81628 3.48438L7.70685 0.719491C8.2731 1.31148 8.2731 2.24428 7.70685 2.83626L4.81628 0.0713733ZM11.8197 11.3577C10.9067 12.3122 10.9067 13.8163 11.8197 14.7708L14.7102 12.0059C15.2765 12.5979 15.2765 13.5307 14.7102 14.1227L11.8197 11.3577ZM9.8201 -0.626957C8.63869 -1.86207 6.66566 -1.86206 5.48425 -0.626956L8.37482 2.13793C7.98101 2.54964 7.32334 2.54964 6.92953 2.13793L9.8201 -0.626957ZM-0.0834904 14.7708C0.888735 15.7872 2.51242 15.7872 3.48465 14.7708L0.594077 12.0059C1.19706 11.3755 2.20409 11.3755 2.80708 12.0059L-0.0834904 14.7708ZM10.4881 3.48438C11.4011 2.52989 11.4011 1.02587 10.4881 0.0713733L7.5975 2.83626C7.03125 2.24428 7.03125 1.31148 7.5975 0.719491L10.4881 3.48438ZM-0.78414 9.89097C-1.89355 11.0508 -1.89355 12.8784 -0.78412 14.0383L2.10645 11.2734C2.47625 11.66 2.47626 12.2692 2.10645 12.6558L-0.78414 9.89097ZM6.91997 3.48443C7.8922 4.5008 9.51585 4.50079 10.4881 3.48438L7.5975 0.719491C8.20047 0.0891097 9.20747 0.0890982 9.81046 0.719462L6.91997 3.48443ZM4.81624 12.5156C3.90326 13.4701 3.90325 14.9741 4.81623 15.9286L7.70682 13.1637C8.27306 13.7557 8.27306 14.6885 7.70681 15.2805L4.81624 12.5156ZM11.8197 9.15845C10.9067 10.1129 10.9067 11.617 11.8197 12.5715L14.7102 9.80656C15.2765 10.3986 15.2765 11.3314 14.7102 11.9233L11.8197 9.15845ZM8.38439 12.5156C7.41216 11.4992 5.78847 11.4992 4.81624 12.5156L7.70681 15.2805C7.10383 15.9109 6.0968 15.9109 5.49382 15.2805L8.38439 12.5156ZM14.7102 11.9233C14.1073 12.5537 13.1002 12.5537 12.4972 11.9233L15.3878 9.15845C14.4156 8.14204 12.7919 8.14203 11.8197 9.15845L14.7102 11.9233Z"
        fill="currentColor"
        mask="url(#path-1-inside-1_299_3146)"
      />
      <path
        d="M2 1C6.4308 15.3893 8.83261 15.9414 13 1"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}
function StatisticsOptionIcon() {
  return (
    <svg
      style={{ display: 'block' }}
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="currentColor"
    >
      <path d="M2.375 7.5C2.75317 7.5 3.09546 7.34827 3.34839 7.10461L4.73596 7.79834C4.729 7.85486 4.71875 7.9104 4.71875 7.96875C4.71875 8.74414 5.34961 9.375 6.125 9.375C6.90039 9.375 7.53125 8.74414 7.53125 7.96875C7.53125 7.75232 7.47803 7.54968 7.39026 7.36633L9.27258 5.48401C9.45593 5.57178 9.65857 5.625 9.875 5.625C10.6504 5.625 11.2812 4.99414 11.2812 4.21875C11.2812 4.07275 11.2526 3.93469 11.2111 3.80225L12.8466 2.57593C13.0697 2.72498 13.3372 2.8125 13.625 2.8125C14.4004 2.8125 15.0312 2.18164 15.0312 1.40625C15.0312 0.630859 14.4004 0 13.625 0C12.8496 0 12.2188 0.630859 12.2188 1.40625C12.2188 1.55225 12.2474 1.69031 12.2889 1.82275L10.6534 3.04907C10.4303 2.90002 10.1628 2.8125 9.875 2.8125C9.09961 2.8125 8.46875 3.44336 8.46875 4.21875C8.46875 4.43518 8.52197 4.63782 8.60974 4.82117L6.72742 6.70349C6.54407 6.61572 6.34143 6.5625 6.125 6.5625C5.74683 6.5625 5.40454 6.71423 5.15161 6.95789L3.76404 6.26416C3.771 6.20764 3.78125 6.1521 3.78125 6.09375C3.78125 5.31836 3.15039 4.6875 2.375 4.6875C1.59961 4.6875 0.96875 5.31836 0.96875 6.09375C0.96875 6.86914 1.59961 7.5 2.375 7.5Z" />
      <path d="M15.5312 15.0625H15.0312V5.15625C15.0312 4.89722 14.8215 4.6875 14.5625 4.6875H12.6875C12.4285 4.6875 12.2188 4.89722 12.2188 5.15625V15.0625H11.2812V7.96875C11.2812 7.70972 11.0715 7.5 10.8125 7.5H8.9375C8.67847 7.5 8.46875 7.70972 8.46875 7.96875V15.0625H7.53125V11.7188C7.53125 11.4597 7.32153 11.25 7.0625 11.25H5.1875C4.92847 11.25 4.71875 11.4597 4.71875 11.7188V15.0625H3.78125V9.84375C3.78125 9.58472 3.57153 9.375 3.3125 9.375H1.4375C1.17847 9.375 0.96875 9.58472 0.96875 9.84375V15.0625H0.46875C0.209717 15.0625 0 15.2722 0 15.5312C0 15.7903 0.209717 16 0.46875 16H15.5312C15.7903 16 16 15.7903 16 15.5312C16 15.2722 15.7903 15.0625 15.5312 15.0625Z" />
    </svg>
  );
}
function ThreeDOptionIcon() {
  return (
    <svg
      style={{ display: 'block' }}
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="currentColor"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M8 14.6667C11.6819 14.6667 14.6667 11.6819 14.6667 8C14.6667 4.3181 11.6819 1.33333 8 1.33333C4.3181 1.33333 1.33333 4.3181 1.33333 8C1.33333 11.6819 4.3181 14.6667 8 14.6667ZM8 16C12.4183 16 16 12.4183 16 8C16 3.58172 12.4183 0 8 0C3.58172 0 0 3.58172 0 8C0 12.4183 3.58172 16 8 16Z"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M1.98124 8.42379C1.9812 8.42378 1.98155 8.42315 1.9824 8.42189C1.9817 8.42317 1.98127 8.42379 1.98124 8.42379ZM2.08069 8.33325H0.666016C0.666016 8.33325 0.666016 8.33325 0.666016 8.33325C0.666016 8.94459 1.1455 9.33611 1.51736 9.55591C1.93386 9.80208 2.49216 10.0007 3.12361 10.1585C4.39678 10.4768 6.12031 10.6666 7.99935 10.6666C9.87839 10.6666 11.6019 10.4768 12.8751 10.1585C13.5065 10.0007 14.0648 9.80208 14.4813 9.55591C14.8532 9.33611 15.3327 8.94459 15.3327 8.33325L13.918 8.33325C13.8885 8.35471 13.8508 8.37974 13.8029 8.40809C13.5447 8.5607 13.1267 8.72126 12.5517 8.865C11.412 9.14993 9.80221 9.33325 7.99935 9.33325C6.19649 9.33325 4.58669 9.14993 3.44699 8.865C2.87202 8.72126 2.45401 8.5607 2.1958 8.40809C2.14785 8.37974 2.11016 8.35471 2.08068 8.33325C2.08068 8.33325 2.08069 8.33325 2.08069 8.33325ZM14.0175 8.42379C14.0174 8.42379 14.017 8.42317 14.0163 8.42189C14.0172 8.42314 14.0175 8.42378 14.0175 8.42379Z"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M2.08135 8.33333H0.666016C0.666016 7.80199 1.03268 7.43771 1.34513 7.22084C1.64759 7.01089 2.06299 7.08589 2.27294 7.38835C2.48288 7.69082 2.40789 8.10621 2.10542 8.31616C2.09693 8.32205 2.08892 8.32778 2.08135 8.33333ZM15.3327 8.33333L13.9173 8.33333C13.9098 8.32778 13.9018 8.32205 13.8933 8.31616C13.5908 8.10621 13.5158 7.69082 13.7258 7.38835C13.9357 7.08589 14.3511 7.01089 14.6536 7.22084C14.966 7.43771 15.3327 7.80199 15.3327 8.33333ZM7.99935 6C7.75189 6 7.50741 6.00329 7.26656 6.00971C6.8985 6.01952 6.60808 6.32585 6.6179 6.69391C6.62771 7.06196 6.93403 7.35238 7.30209 7.34257C7.53106 7.33647 7.76369 7.33333 7.99935 7.33333C8.235 7.33333 8.46764 7.33647 8.69661 7.34257C9.06467 7.35238 9.37099 7.06196 9.3808 6.69391C9.39062 6.32585 9.1002 6.01952 8.73214 6.00971C8.49129 6.00329 8.24681 6 7.99935 6ZM5.22403 7.48869C5.58966 7.44533 5.85091 7.11378 5.80755 6.74815C5.76418 6.38252 5.43263 6.12127 5.06701 6.16463C4.54117 6.22699 4.04799 6.30598 3.59813 6.39971C3.23768 6.4748 3.00635 6.82788 3.08144 7.18833C3.15654 7.54878 3.50962 7.78011 3.87007 7.70501C4.27687 7.62026 4.73128 7.54713 5.22403 7.48869ZM10.9317 6.16463C10.5661 6.12127 10.2345 6.38252 10.1912 6.74815C10.1478 7.11378 10.409 7.44533 10.7747 7.48869C11.2674 7.54713 11.7218 7.62026 12.1286 7.70501C12.4891 7.78011 12.8422 7.54878 12.9173 7.18833C12.9923 6.82788 12.761 6.4748 12.4006 6.39971C11.9507 6.30598 11.4575 6.22699 10.9317 6.16463Z"
      />
    </svg>
  );
}

function isIOS() {
  if (navigator.userAgent.match(/(iPad|iPhone)/) !== null) return true;
  // iPad OS >= 13 pretends to be a Mac now
  if (navigator.userAgent.includes('Mac') && 'ontouchend' in document)
    return true;
  return false;
}

function clamp(min: number, max: number, x: number) {
  return x < min ? min : x > max ? max : x;
}

// Geogebra has a bootstrapping script which then subsequently
// pulls down the massive geogebra javascript files. We lazily
// pull down this bootstrapping script as part of the component
// initialization so that this dependency is properly encoded as
// a dependency of the consuming component (rather than doing
// something brittle and error prone like randomly loading this
// bootstrapping script in a template file). An easy way to
// understand why we do it this way is that if Geogebra was
// a well-made project, the dependency would be encoded like so:
// import { GGBApplet } from 'geogebra';
let bootstrapPromise: Promise<any> | null = null;
function loadBootstrapScript() {
  if (bootstrapPromise !== null) return bootstrapPromise;
  bootstrapPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://www.geogebra.org/apps/deployggb.js';
    script.onload = () => {
      // The script injects this window global. We don't want any other
      // code to know about it so we won't add it to the Window interface
      // @ts-expect-error
      const { GGBApplet } = window;
      resolve(GGBApplet);
    };
    script.onerror = error => reject(error);
    if (document.body) document.body.appendChild(script);
  });
  return bootstrapPromise;
}
